import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Alert,
  Grid,
  Box,
  Grow
} from "@mui/material";

import axiosInstance from "../../utils/axiosInstance";
import appConfig from "../../config/appConfig";

const MessagingApiConfigForm = ({
  open,
  onClose,
  initialValues = {},
  isUpdate = false,
  refreshConfigs,
  setSnackbar,
}) => {
  const [formValues, setFormValues] = useState({
    name: "",
    apiKey: "",
    apiSecret: "",
    viberId: "",
    viberUserId: "",
    endpoint: "",
    apiProvider: "",
    smsSenderId: "",
    authenticationType: "APIKey",
    certificate: "",
    authorizationUrl: "",
    status: "Inactive",
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!isUpdate) {
      setFormValues({
        name: "",
        apiKey: "",
        apiSecret: "",
        viberId: "",
        viberUserId: "",
        endpoint: "",
        apiProvider: "",
        smsSenderId: "",
        authenticationType: "APIKey",
        certificate: "",
        authorizationUrl: "",
        status: "Inactive",
      });
    } else {
      setFormValues({
        configId: initialValues.configId || "",
        name: initialValues.name || "",
        apiKey: initialValues.apiKey || "",
        apiSecret: initialValues.apiSecret || "",
        viberId: initialValues.viberId || "",
        viberUserId: initialValues.viberUserId || "",
        endpoint: initialValues.endpoint || "",
        apiProvider: initialValues.apiProvider || "",
        smsSenderId: initialValues.smsSenderId || "",
        authenticationType: initialValues.authenticationType || "APIKey",
        certificate: initialValues.certificate || "",
        authorizationUrl: initialValues.authorizationUrl || "",
        status: initialValues.status || "Inactive",
      });
    }
    setValidationErrors({});
  }, [open, isUpdate, initialValues]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.name.trim()) errors.name = "Name is required.";

    if (!formValues.endpoint.trim()) {
      errors.endpoint = "Endpoint is required.";
    } else {
      try {
        new URL(formValues.endpoint);
      } catch (error) {
        errors.endpoint = "Endpoint must be a valid URL.";
      }
    }

    if (!formValues.apiProvider) errors.apiProvider = "API Provider is required.";

    if (formValues.authenticationType === "APIKey") {
      if (!formValues.apiKey.trim()) errors.apiKey = "API Key is required.";
      if (!formValues.apiSecret.trim()) errors.apiSecret = "API Secret is required.";
    }

    if (formValues.authenticationType === "Certificate") {
      if (!formValues.certificate.trim()) errors.certificate = "Certificate is required.";
      if (!formValues.authorizationUrl.trim()) {
        errors.authorizationUrl = "Authorization URL is required.";
      } else {
        try {
          new URL(formValues.authorizationUrl);
        } catch (error) {
          errors.authorizationUrl = "Authorization URL must be a valid URL.";
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;


    // Create a copy of formValues to modify before sending
    const payload = { ...formValues };

    // Remove authorizationUrl if authenticationType is not 'Certificate'
    if (payload.authenticationType !== "Certificate") {
      delete payload.authorizationUrl;
    }


    setLoading(true);
    try {
      if (isUpdate) {
        await axiosInstance.put(
          `${appConfig.endpoints.messagingApiConfig.byId(initialValues.configId)}`,
          payload
        );
      } else {
        await axiosInstance.post(
          appConfig.endpoints.messagingApiConfig.base,
          payload
        );
      }

      if (refreshConfigs) refreshConfigs();

      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: isUpdate
            ? "Configuration updated successfully!"
            : "Configuration added successfully!",
          severity: "success",
        });
      }

      onClose();
    } catch (error) {
      console.error("Error submitting Messaging API Config data:", error);

      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: error.response?.data
            ? `${error.response.data.message}: ${error.response.data.error}`
            : "Failed to submit configuration data.",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isUpdate ? "Update Configuration" : "Add Configuration"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Endpoint"
                type="text"
                fullWidth
                name="endpoint"
                value={formValues.endpoint}
                onChange={handleInputChange}
                error={!!validationErrors.endpoint}
                helperText={validationErrors.endpoint}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="API Provider"
                name="apiProvider"
                value={formValues.apiProvider || ""}
                onChange={handleInputChange}
                select
                fullWidth
                required
                error={!!validationErrors.apiProvider}
                helperText={validationErrors.apiProvider}
              >
                <MenuItem value="Promotext">Promotext</MenuItem>
                <MenuItem value="Viber">Viber</MenuItem>
                <MenuItem value="Line">Line</MenuItem>
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="Twilio">Twilio</MenuItem>
                <MenuItem value="AWS">AWS</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Authentication Type"
                name="authenticationType"
                value={formValues.authenticationType}
                onChange={handleInputChange}
                select
                fullWidth
                required
              >
                <MenuItem value="APIKey">API Key</MenuItem>
                <MenuItem value="Certificate">Certificate</MenuItem>
                <MenuItem value="OAuth">OAuth</MenuItem>
                <MenuItem value="BasicAuth">Basic Auth</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            {formValues.authenticationType === "APIKey" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="API Key"
                    type="text"
                    fullWidth
                    name="apiKey"
                    value={formValues.apiKey}
                    onChange={handleInputChange}
                    error={!!validationErrors.apiKey}
                    helperText={validationErrors.apiKey}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="API Secret"
                    type="text"
                    fullWidth
                    name="apiSecret"
                    value={formValues.apiSecret}
                    onChange={handleInputChange}
                    error={!!validationErrors.apiSecret}
                    helperText={validationErrors.apiSecret}
                    required
                  />
                </Grid>
              </>
            )}
            {formValues.authenticationType === "Certificate" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Certificate"
                    type="text"
                    fullWidth
                    name="certificate"
                    value={formValues.certificate}
                    onChange={handleInputChange}
                    error={!!validationErrors.certificate}
                    helperText={validationErrors.certificate}
                    required
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Authorization URL"
                    type="text"
                    fullWidth
                    name="authorizationUrl"
                    value={formValues.authorizationUrl}
                    onChange={handleInputChange}
                    error={!!validationErrors.authorizationUrl}
                    helperText={validationErrors.authorizationUrl}
                    required
                  />
                </Grid>
              </>
            )}

            {formValues.apiProvider === "Promotext" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Viber User Id"
                    type="text"
                    fullWidth
                    name="viberUserId"
                    value={formValues.viberUserId}
                    onChange={handleInputChange}
                    error={!!validationErrors.viberUserId}
                    helperText={validationErrors.viberUserId}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Channel ID"
                    type="text"
                    fullWidth
                    name="viberId"
                    value={formValues.viberId}
                    onChange={handleInputChange}
                    error={!!validationErrors.viberId}
                    helperText={validationErrors.viberId}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="SMS Sender Name"
                    type="text"
                    fullWidth
                    name="smsSenderId"
                    value={formValues.smsSenderId}
                    onChange={handleInputChange}
                    error={!!validationErrors.smsSenderId}
                    helperText={validationErrors.smsSenderId}
                  />
                </Grid>
              </>
            )}
            {isUpdate && (
              <Grid item xs={12}>
                <TextField
                  label="Status"
                  name="status"
                  value={formValues.status || ""}
                  onChange={handleInputChange}
                  select
                  fullWidth
                  required
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isUpdate ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessagingApiConfigForm;